import * as React from 'react'
import {
  NumberField as AriaNumberField,
  Group,
  Input as AriaInput,
  Button as AriaButton,
  type NumberFieldProps as AriaNumberFieldProps,
} from 'react-aria-components'
import { Minus, Plus } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * NumberField — a numeric input with −/+ steppers, built on
 * react-aria-components (arrow-key step, scroll-to-change, locale-aware
 * formatting). The box reuses our `Input` look; the steppers are divider-
 * separated ghost buttons that flank a centered value.
 *
 *   <NumberField defaultValue={1} minValue={0} maxValue={10} />
 *   <NumberField formatOptions={{ style: 'currency', currency: 'USD' }} />
 *
 * `step`, `minValue`/`maxValue`, and `formatOptions` (Intl.NumberFormat) pass
 * through. Boolean props follow DS naming and map to react-aria's `is*`.
 */

type FieldSize = 'sm' | 'md' | 'lg'
type FieldStatus = 'default' | 'error' | 'success'

const groupSize: Record<FieldSize, string> = {
  sm: 'h-8 text-xs',
  md: 'h-9 text-sm',
  lg: 'h-10 text-base',
}

const groupBase =
  'inline-flex w-36 items-stretch overflow-hidden rounded-lg border bg-fbui-base-white text-fbui-gray-800 transition-colors focus-within:ring-4 data-[disabled]:cursor-not-allowed data-[disabled]:bg-fbui-gray-50 data-[disabled]:text-fbui-text-disabled'

const groupStatus = {
  default:
    'border-fbui-gray-300 focus-within:border-fbui-basil-700 focus-within:ring-fbui-ring-brand',
  error:
    'border-fbui-red-500 focus-within:border-fbui-red-600 focus-within:ring-fbui-ring-error',
  success:
    'border-fbui-green-500 focus-within:border-fbui-green-600 focus-within:ring-fbui-ring-success',
} as const

const stepperBase =
  'inline-flex shrink-0 items-center justify-center px-2.5 text-fbui-gray-500 outline-none transition-colors data-[hovered]:bg-fbui-gray-100 data-[hovered]:text-fbui-gray-700 data-[pressed]:bg-fbui-gray-200 data-[focus-visible]:ring-2 data-[focus-visible]:ring-inset data-[focus-visible]:ring-fbui-ring-brand data-[disabled]:text-fbui-gray-300'

export interface NumberFieldProps extends Omit<
  AriaNumberFieldProps,
  'isDisabled' | 'isReadOnly' | 'isRequired' | 'isInvalid' | 'className'
> {
  size?: FieldSize
  status?: FieldStatus
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  invalid?: boolean
  /** Classes on the outer group (e.g. a width override). */
  className?: string
}

export const NumberField = React.forwardRef<HTMLDivElement, NumberFieldProps>(
  (
    {
      size = 'md',
      status,
      disabled,
      readOnly,
      required,
      invalid,
      className,
      ...props
    },
    ref
  ) => {
    const resolved = invalid ? 'error' : (status ?? 'default')
    return (
      <AriaNumberField
        ref={ref}
        isDisabled={disabled}
        isReadOnly={readOnly}
        isRequired={required}
        isInvalid={invalid}
        {...props}
      >
        <Group
          className={cn(
            groupBase,
            groupSize[size],
            groupStatus[resolved],
            className
          )}
        >
          <AriaButton
            slot="decrement"
            aria-label="Decrease"
            className={cn(stepperBase, 'border-r border-fbui-gray-200')}
          >
            <Minus weight="bold" className="size-3.5" aria-hidden="true" />
          </AriaButton>
          <AriaInput className="w-full min-w-0 flex-1 bg-transparent px-2 text-center text-fbui-gray-800 outline-none placeholder:text-fbui-gray-400 disabled:cursor-not-allowed" />
          <AriaButton
            slot="increment"
            aria-label="Increase"
            className={cn(stepperBase, 'border-l border-fbui-gray-200')}
          >
            <Plus weight="bold" className="size-3.5" aria-hidden="true" />
          </AriaButton>
        </Group>
      </AriaNumberField>
    )
  }
)
NumberField.displayName = 'NumberField'
